/**
 * Текст ошибки запроса RTK Query — всегда строка, пригодная для показа.
 *
 * Бэкенд отвечает `{ error: { code, message } }` (`osnova-api/src/error.rs`), а
 * `parseResponse` приводит к той же форме и отказы nginx (413/502/504). Страницы
 * же по старой памяти брали `e.data.error` как строку и клали в state: объект
 * уходил в JSX, React падал «Objects are not valid as a React child», и без
 * ErrorBoundary пользователь видел белый экран вместо «нельзя принять рапорт из
 * этого статуса». Поэтому разбор — здесь, один на всё приложение.
 *
 * `message` — это Display `AppError` со служебным префиксом («conflict: …»,
 * «validation: …», `osnova-core/src/error.rs`): префикс снимаем. Внутренние
 * ошибки (текст SQL, сериализации) пользователю ничего не скажут — вместо них
 * fallback с пометкой «внутренняя ошибка сервера».
 */

/** Служебные префиксы Display у `AppError` (`osnova-core/src/error.rs`). */
const PREFIX =
  /^(not found|conflict|validation|bad request|upstream error|service unavailable|database error|internal error):\s*/i;

/** Коды, текст которых — внутренности сервера (SQL, паники), а не причина для человека. */
const INTERNAL_CODES = new Set(['internal_error', 'database_error']);

/** HTTP-статус ошибки или код RTK Query (`FETCH_ERROR`, `TIMEOUT_ERROR`, …). */
export function apiErrorStatus(err: unknown): number | string | undefined {
  if (typeof err !== 'object' || err === null || !('status' in err)) return undefined;
  const s = (err as { status?: unknown }).status;
  return typeof s === 'number' || typeof s === 'string' ? s : undefined;
}

/** Тело ошибки ответа (`data`), если это объект. */
function errorBody(err: unknown): Record<string, unknown> | null {
  if (typeof err !== 'object' || err === null || !('data' in err)) return null;
  const data = (err as { data?: unknown }).data;
  return typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : null;
}

/** Код ошибки бэкенда (`conflict`, `validation_error`, …), если есть. */
export function apiErrorCode(err: unknown): string | null {
  const inner = errorBody(err)?.error;
  if (typeof inner !== 'object' || inner === null) return null;
  const code = (inner as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/** Сообщение сервера: `{error:{message}}`, `{error:"…"}` или `{message}`. */
function serverMessage(err: unknown): string | null {
  const body = errorBody(err);
  if (!body) return null;
  const inner = body.error;
  const raw =
    typeof inner === 'string'
      ? inner
      : typeof inner === 'object' && inner !== null
        ? (inner as { message?: unknown }).message
        : body.message;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** Fallback как законченное предложение — чтобы дописать к нему причину. */
function sentence(s: string): string {
  const t = s.trim();
  return /[.!?…]$/.test(t) ? t : `${t}.`;
}

/**
 * Человекочитаемая причина ошибки запроса. `fallback` — что сказать, если сервер
 * причину не прислал (или прислал внутренности): «Не удалось принять рапорт.».
 */
export function apiErrorText(err: unknown, fallback: string): string {
  const status = apiErrorStatus(err);
  if (status === 'FETCH_ERROR') return 'Нет связи с сервером. Повторите позже.';
  if (status === 'TIMEOUT_ERROR') return 'Сервер не ответил вовремя. Повторите позже.';
  const code = apiErrorCode(err);
  if (code && INTERNAL_CODES.has(code)) {
    return `${sentence(fallback)} Внутренняя ошибка сервера — повторите позже или сообщите администратору.`;
  }
  const message = serverMessage(err);
  // Голые «forbidden»/«unauthorized» без пояснения — переводим, с пояснением — показываем.
  if (status === 403 && (!message || /^forbidden$/i.test(message))) {
    return 'Недостаточно прав для этого действия.';
  }
  if (status === 401 && (!message || /^unauthorized$/i.test(message))) {
    return 'Сессия истекла — войдите в систему заново.';
  }
  // У этих кодов Display без причины («payload too large», «rate limit exceeded»).
  if (code === 'payload_too_large') {
    return `${sentence(fallback)} Файл слишком большой для сервера.`;
  }
  if (code === 'rate_limit') {
    return `${sentence(fallback)} Слишком много запросов — повторите через минуту.`;
  }
  if (message) {
    const text = capitalize(message.replace(PREFIX, '').trim());
    // Отказ инфраструктуры (`http_502` и т.п. из `parseResponse`) не говорит, ЧТО
    // не удалось, — добавляем это из fallback; причина бэкенда говорит сама.
    if (text && code?.startsWith('http_')) return `${sentence(fallback)} ${text}`;
    if (text) return text;
  }
  if (typeof status === 'number' && status >= 500) {
    return `${sentence(fallback)} Ошибка сервера (${status}) — повторите позже.`;
  }
  return fallback;
}

/**
 * Пояснение сервера как есть — без служебного префикса, с заглавной буквы.
 * Нет пояснения или это внутренности сервера (SQL, паника) — `null`: тогда
 * место говорит своё (например, 413 у архива — «больше 10 ГБ…»).
 */
export function apiErrorMessage(err: unknown): string | null {
  const code = apiErrorCode(err);
  if (code && INTERNAL_CODES.has(code)) return null;
  const message = serverMessage(err);
  if (!message) return null;
  const text = capitalize(message.replace(PREFIX, '').trim());
  return text || null;
}

/**
 * Текст ошибки ЗАГРУЗКИ (просмотр, GET). Отказ в доступе без пояснения сервера —
 * `forbidden` («Нет доступа к площадке…»): «Недостаточно прав для этого
 * действия» при простом открытии страницы сбивает с толку — человек ничего не
 * делал. Остальное — как [`apiErrorText`].
 */
export function apiLoadErrorText(err: unknown, fallback: string, forbidden: string): string {
  const message = serverMessage(err);
  if (apiErrorStatus(err) === 403 && (!message || /^forbidden$/i.test(message))) {
    return forbidden;
  }
  return apiErrorText(err, fallback);
}

/**
 * То же для выгрузок (XLSX/PDF/DOCX): у них `responseHandler` читает тело как
 * Blob — и тело ошибки тоже, поэтому `{error:{message}}` лежит внутри Blob.
 * Достаём его и разбираем как обычно; не JSON — fallback.
 */
export async function blobErrorText(err: unknown, fallback: string): Promise<string> {
  const data =
    typeof err === 'object' && err !== null ? (err as { data?: unknown }).data : undefined;
  if (typeof Blob !== 'undefined' && data instanceof Blob && data.type.includes('json')) {
    try {
      const parsed: unknown = JSON.parse(await data.text());
      return apiErrorText({ status: apiErrorStatus(err), data: parsed }, fallback);
    } catch {
      return apiErrorText({ status: apiErrorStatus(err) }, fallback);
    }
  }
  return apiErrorText(err, fallback);
}
